/**
 * /integrations — front-door registry (Slack today), in the house "reviewed
 * repos" idiom: bordered table, `Switch` for admins / `Chip` for read-only,
 * an uppercase mono caption under each switch stating the consequence.
 *
 * Reads open to any authenticated user; the switch and Stop are admin-only
 * (backend enforces; FE hides the controls and shows a read-only Alert).
 * Stop is destructive for in-flight work (drops queued/running turns), so
 * it confirms via dialog.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { useState, type ReactElement } from 'react';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { useIntegrations } from '@/hooks/useIntegrations';
import type { IntegrationStatus } from '@/types/integrations';
import { formatRelative } from '@/utils/formatRelative';

const SKELETON_ROWS = 2;

function TableHeadRow({ canEdit }: { canEdit: boolean }): ReactElement {
  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>Front door</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', width: '40%' }}>Listening</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>Today</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>Threads (24h)</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>Last event</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>Last error</TableCell>
      {canEdit && <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>}
    </TableRow>
  );
}

export function IntegrationsPage(): ReactElement {
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);
  const { rows, loading, error, refresh, setEnabled, stop } = useIntegrations();

  const [busy, setBusy] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<IntegrationStatus | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleToggle = async (row: IntegrationStatus, enabled: boolean): Promise<void> => {
    setBusy(row.adapter);
    try {
      await setEnabled(row.adapter, enabled);
      setNotice(`${row.adapter} is ${enabled ? 'listening' : 'off'}`);
    } catch {
      // the hook already recorded the error in its own state (page Alert below)
    } finally {
      setBusy(null);
    }
  };

  const handleStop = async (): Promise<void> => {
    if (!confirmTarget) return;
    setBusy(confirmTarget.adapter);
    try {
      const dropped = await stop(confirmTarget.adapter);
      setNotice(`Stopped ${confirmTarget.adapter} — dropped ${dropped} in-flight turn${dropped === 1 ? '' : 's'}.`);
      setConfirmTarget(null);
    } catch {
      // keep the dialog open; the hook's error state (page Alert) shows what happened
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'primary.main' }}>
          integrations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Front doors Sherlock listens on.
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="large" aria-label="Refresh integrations" onClick={() => void refresh()}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {!canEdit && (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          Read-only view. Switching a front door on or off is restricted to admins.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refresh()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" aria-label="Front doors">
            <TableHead>
              <TableHeadRow canEdit={canEdit} />
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
                <TableRow key={rowIdx}>
                  {Array.from({ length: canEdit ? 7 : 6 }).map((_, colIdx) => (
                    <TableCell key={colIdx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : !loading && !error && rows.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No front doors registered.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adding one is a code change — see docs/architecture/conversation-engine.md.
          </Typography>
        </Box>
      ) : !loading && error && rows.length === 0 ? null : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" aria-label="Front doors">
            <TableHead>
              <TableHeadRow canEdit={canEdit} />
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.adapter} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {row.adapter}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: '40%' }}>
                    <Stack spacing={0.25} alignItems="flex-start">
                      {canEdit ? (
                        <Switch
                          checked={row.enabled}
                          disabled={busy === row.adapter}
                          onChange={(e) => void handleToggle(row, e.target.checked)}
                          inputProps={{ 'aria-label': `Listening on ${row.adapter}` }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={row.enabled ? 'listening' : 'off'}
                          color={row.enabled ? 'success' : 'default'}
                          variant={row.enabled ? 'filled' : 'outlined'}
                        />
                      )}
                      {row.enabled ? (
                        <Typography
                          variant="caption"
                          sx={{ color: 'success.main', textTransform: 'uppercase', fontFamily: 'monospace' }}
                        >
                          listening — mentions are answered
                        </Typography>
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', textTransform: 'uppercase', fontFamily: 'monospace' }}
                          >
                            off — messages stored, nothing runs
                          </Typography>
                          {row.paused_by && row.paused_at && (
                            <Tooltip
                              title={`paused by ${row.paused_by} · ${formatRelative(row.paused_at)}${row.note ? ` · ${row.note}` : ''}`}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: 'none' }}
                              >
                                paused by {row.paused_by} · {formatRelative(row.paused_at)}
                                {row.note && (
                                  <Box
                                    component="span"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'inline-block',
                                      maxWidth: 200,
                                      verticalAlign: 'bottom',
                                    }}
                                  >
                                    {` · ${row.note}`}
                                  </Box>
                                )}
                              </Typography>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" className="tnum">
                      {row.turns_today} turns
                    </Typography>
                    <Typography variant="body2" className="tnum">
                      ${row.spend_today_usd.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" className="tnum">
                      {row.threads_active}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.last_event_at ? (
                      <Tooltip title={new Date(row.last_event_at).toLocaleString()}>
                        <Typography variant="body2" color="text.secondary">
                          {formatRelative(row.last_event_at)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.last_error ? (
                      <Tooltip title={row.last_error}>
                        <Typography
                          variant="body2"
                          color="error.main"
                          sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {row.last_error}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<StopCircleOutlinedIcon />}
                        sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
                        disabled={busy === row.adapter || (!row.enabled && row.threads_active === 0)}
                        onClick={() => setConfirmTarget(row)}
                      >
                        Stop now
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmTarget !== null} onClose={() => setConfirmTarget(null)}>
        <DialogTitle>Stop {confirmTarget?.adapter} now?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Off stops new work. Stop also drops every turn that is queued or running right now, and
            switches {confirmTarget?.adapter} off. Messages people send while it is off are kept but not
            answered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={busy === confirmTarget?.adapter}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleStop()}
            disabled={busy === confirmTarget?.adapter}
          >
            Stop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
